import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { NotificationsWebService } from './@shared/services/notifications.web.service';
import { environment } from '@env/environment';
import { Logger, UntilDestroy, untilDestroyed } from '@shared';
import { I18nService } from '@app/i18n';
import { ModalController, NavController, Platform } from '@ionic/angular';
// import { SplashComponent } from './@shared/components/splash/splash.component';
import { App as CapacitorApp } from '@capacitor/app';
import { AddFundsComponent } from './wallet/add-funds/add-funds.component';
import { TransactionService } from './@shared/services/transaction.service';
import { WalletApiService } from './@shared/services/wallet-api.service';
import { WalletTypeService } from './@shared/services/wallet-type.service';
import { WalletService } from './@shared/services/wallet.service';
import { Credentials, CredentialsService } from './auth';
import { NotificationsService } from './@shared/services/notifications.service';
import { Capacitor } from '@capacitor/core';
import { PayoutService } from './@shared/services/payout.service';
import { PayoutComponent } from './wallet/payout/payout.component';
import { StatusBar, Style } from '@capacitor/status-bar';

import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { IpApiService } from './@shared/services/ip-api/ip-api.service';
import { TrackingService } from './@shared/services/tracking.service';

import { AndroidPermissions } from '@awesome-cordova-plugins/android-permissions/ngx';
import { NuveiService } from './@shared/services/nuvei.service';
import { SpeiService } from './@shared/services/spei.service';
import { TopService } from './@shared/services/top.service';
import { CampaignService } from './@shared/services/campaign.service';
import { CampaingConversion } from './@shared/models/tracking.model';

import { LocationService } from './@shared/services/location.service';
import { AuthService } from './@shared/services/auth.service';
import { CardService } from './@shared/services/card.service';
import { UserService } from './@shared/services/user.service';
import { AlertService } from './@shared/services/alert.service';

const log = new Logger('App');

@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    public analytics: AngularFireAnalytics,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private i18nService: I18nService,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private walletApiS: WalletApiService,
    private walletTypeS: WalletTypeService,
    private walletS: WalletService,
    private transactionS: TransactionService,
    private credentialsS: CredentialsService,
    private platform: Platform,
    private notificationsS: NotificationsService,
    private notificationsWeb: NotificationsWebService,
    private payoutS: PayoutService,
    private ipApiService: IpApiService,
    private trackS: TrackingService,
    private nuveiS: NuveiService,
    private speiS: SpeiService,
    private TopS: TopService,
    private campaignS: CampaignService,
    private cardS: CardService,
    private androidPermissions: AndroidPermissions,
    private locationS: LocationService,
    private authService: AuthService,
    private userS: UserService,
    private alertS: AlertService
  ) {
    this.analytics.logEvent('app_open', { component: 'AppComponent' });
    this.trackCampaign();
    CapacitorApp.addListener('backButton', async ({ canGoBack }) => {
      if (!canGoBack) {
        CapacitorApp.exitApp();
      } else {
        this.navCtrl.back();
      }
    });

    this.initializeApp();
  }

  async initializeApp() {
    // await this.ipApiService.getCountry();
    // console.log('Country code:', localStorage.getItem('isAllowedCountry'));
    await this.getLocationData();

    this.platform.ready().then(() => {
      const isNative = Capacitor.isNativePlatform();

      if (isNative) {
        this.notificationsS.initPush();
        this.requestStoragePermissions();
      } else {
        this.notificationsWeb.requestPermissionAndSaveToken();
        this.notificationsWeb.listenToForeground();
      }
    });

    // Solo para iOS
    if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
      StatusBar.setOverlaysWebView({ overlay: false }); // Para que no se meta el notch encima
      StatusBar.setStyle({ style: Style.Light }); // O dark dependiendo de tu app
    }
  }

  async requestStoragePermissions() {
    const result = await this.androidPermissions.checkPermission(
      this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE
    );

    if (!result.hasPermission) {
      const permission = await this.androidPermissions.requestPermission(
        this.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE
      );

      if (permission.hasPermission) {
        log.info('✅ Permiso de escritura concedido');
      } else {
        log.warn('❌ Permiso de escritura denegado');
      }
    }
  }

  ngOnInit() {
    // Setup logger
    if (environment.production) {
      Logger.enableProductionMode();
    }

    log.debug('init');

    // Setup translations
    this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);

    // Establecer título por defecto
    this.titleService.setTitle('CMillonario - Jugar es fácil');

    const onNavigationEnd = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // Garantía extra: si por cualquier razón quedamos en "/", forzar "/home"
    onNavigationEnd.pipe(untilDestroyed(this)).subscribe((event) => {
      const nav = event as NavigationEnd;
      if (nav.urlAfterRedirects === '/' || nav.urlAfterRedirects === '') {
        this.router.navigateByUrl('/home', { replaceUrl: true });
      }
    });

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        switchMap((route) => route.data),
        untilDestroyed(this)
      )
      .subscribe((event) => {
        const title = event['title'];
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });

    // Nota: La redirección inicial de "/" a "/home" se maneja en app-routing.module.ts
    // con pathMatch: 'full', lo que permite que otras rutas como /legal funcionen correctamente

    // actualizar login values on user
    this.updateUserLogin();
    this.showAlert();
  }

  async showAlert() {
    const credentials = this.credentialsS.credentials;

    if (credentials) {
      const startAt = new Date();
      startAt.setMonth(startAt.getMonth() - 1);
      startAt.setHours(0, 0, 0, 0);

      const endAt = new Date();
      endAt.setHours(23, 59, 59, 999);

      const transactions =
        (await this.transactionS.getByCustomerIdAndRange(this.credentialsS.credentials!.id!, startAt, endAt)) ?? [];

      const completed = transactions.filter((t) => t.status === 'completed');
      const pending = transactions
        .filter((t) => t.status === 'pending')
        .map((t) => {
          if (completed.some((c) => c.refunds?.length && c.refunds.some((d) => d.id === t.uid))) {
            t.status = 'completed';
          }
          return t;
        });
      const declined = transactions.filter((t) => t.status === 'declined');
      const created = transactions.filter((t) => t.status === 'created');
      const cancelled = transactions.filter((t) => t.status === 'cancelled');
      const system_error = transactions.filter((t) => t.status === 'system_error');

      log.info('transactions', transactions.length);

      await this.alertS.presentActionSheet(credentials, [
        ...completed,
        ...pending.filter((t) => t.data?.save?.method !== 'apmgw_STPmex'),
        ...created.filter((t) => t.type.includes('withdrawal')),
        ...system_error,
        ...cancelled,
        ...declined,
      ]);
    }
  }

  async updateUserLogin() {
    const credentials = this.credentialsS.credentials;
    if (credentials) {
      let { user } = credentials;

      user = await this.userS.updateLoginAt(user);

      credentials.user = user;
      this.credentialsS.setCredentials(credentials);
    }
  }

  async getLocationData() {
    if (!this.isWeb) {
      const data = await this.ipApiService.getIpData(true);
      if (!data) {
        //opciones con location service
        await this.locationS.getLocation(true);
      }

      //revisar si hay user logueado
      const credentials = this.credentialsS.credentials;
      if (credentials) {
        //revisar que se este en mexico
        const isMx = (await this.ipApiService.isMx()) || (await this.locationS.isMx());
        if (!isMx) {
          // si no esta dentro de mexico, sacar logout
          this.authService.logOut();
        }
      }
    }
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }

  get isWeb(): boolean {
    return !this.platform.is('cordova');
  }

  async getLimitForAddFunds(credentials: Credentials) {
    // obtener el limite segun el nuvel de usuario
    const top = await this.TopS.getTop(credentials.id);
    log.info(top);

    // usuario level 0 (sin verificacion minima)
    let limit = 700;

    if (top) {
      //usuario top level 3
      limit = 0;
    } else {
      // level 2 con documents verificado (estado cuenta, tarjeta otros)
      if (credentials.customer.documentVerified) {
        limit = 10000;
      }

      //level 1 con verificacion basica (ine,numero y email)
      else if (
        credentials.customer.emailVerified &&
        credentials.customer.phoneVerified &&
        credentials.customer.idVerified
      ) {
        limit = 2500;
      }
    }

    // get transaction de los ultimos 30 dias que sean recargas completas y pendientes validas
    const transactions = await this.transactionS.getTransactionAddFundsLimit(credentials.id);
    const filtered = transactions.filter(
      (t) => (t.status === 'pending' && t.data?.save?.method !== 'apmgw_STPmex') || t.status === 'completed'
    );

    const total = filtered.reduce((a, b) => a + b.amount, 0);

    if (total) {
      if (total >= limit) return -1;
      return limit - total;
    }

    return limit;
  }

  async openAddFunds() {
    const credentials = this.credentialsS.credentials;

    if (!credentials) return;

    // const limit = await this.getLimitForAddFunds(credentials);

    // if (limit < 100) {
    //   const alert = await this.alertCtrl.create({
    //     header: this.translateService.instant('nuvei.limit-excedeed'),
    //     buttons: [this.translateService.instant('Ok')],
    //   });

    //   await alert.present();

    //   return;
    // }

    // const level =
    // const bp = 0.65;
    const modal = await this.modalCtrl.create({
      backdropDismiss: false,
      component: AddFundsComponent,
      componentProps: {
        walletApiS: this.walletApiS,
        walletS: this.walletS,
        walletTypeS: this.walletTypeS,
        credentials: this.credentialsS.credentials,
        nuveiS: this.nuveiS,
        transactionS: this.transactionS,
        topS: this.TopS,
        cardS: this.cardS,
        // limit,
      },
      cssClass: 'add-funds-modal',
      // initialBreakpoint: bp,
      // breakpoints: [0, bp, 1],
    });

    await modal.present();

    const { role } = await modal.onWillDismiss();
    this.transactionS.refresh.emit(true);

    if (role === 'expired') {
      this.openAddFunds();
    }
  }

  async openPayout() {
    const credentials = this.credentialsS.credentials;

    if (!credentials) return;

    const min = 100;

    const wllets = await this.walletS.getByCustomerId(credentials.id);
    const awardWallets = wllets.filter((w) => w.walletName.includes('award'));
    const balance = awardWallets.reduce((a, b) => a + b.balance, 0);

    if (!awardWallets.length || balance < min) return;

    // const bp = 0.65;
    const modal = await this.modalCtrl.create({
      backdropDismiss: false,
      component: PayoutComponent,
      componentProps: {
        payoutS: this.payoutS,
        walletS: this.walletS,
        credentials: this.credentialsS.credentials,
        transactionS: this.transactionS,
        nuveiS: this.nuveiS,
        speiS: this.speiS,
      },
      cssClass: 'add-funds-modal',
      // initialBreakpoint: bp,
      // breakpoints: [0, bp, 1],
    });

    await modal.present();

    const { role } = await modal.onWillDismiss();

    this.transactionS.refresh.emit(true);

    if (role === 'expired') {
      this.openAddFunds();
    }
  }

  trackCampaign() {
    const utmParams = this.campaignS.getUTMParams();
    if (!utmParams || Object.values(utmParams).every((value) => value === null)) {
      //log.info('UTM Params are null or empty.');
      return;
    }

    this.trackS.trackEvent(
      'campaign_click',
      {
        ...utmParams,
      },
      false,
      true
    );
  }
  trackCampaignConversion(type: CampaingConversion, extraData: any = {}) {
    const utmParams = this.campaignS.getUTMParams();

    if (!utmParams || Object.values(utmParams).every((value) => value === null)) {
      //console.log('UTM Params are null or empty. Skipping campaign_conversion.');
      return;
    }

    this.trackS.trackEvent(
      'campaign_conversion',
      {
        ...utmParams,
        conversion_type: type,
        ...extraData,
      },
      false,
      true
    );
  }
}
